import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon, Popup, TextArea } from "semantic-ui-react";
import { toast } from "react-toastify";
import { RiskAssessment } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import "./RiskAssessmentsTable.scss";

const riskAssessmentController = new RiskAssessment();

const AVATAR_COLORS = ["#1A5FA0", "#0E376E", "#3D6B9E", "#648EBC", "#4F7FB5", "#2A4F80"];

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function avatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ── Helpers de etiquetas ────────────────────────────────────
const HAZARD_LABELS = {
  machinery_equipment:            "Máquina / Equipo",
  hand_electric_tool:             "Herramienta manual",
  electrical_installation:        "Inst. eléctrica",
  hazardous_chemical:             "Sust. química",
  physical_agent:                 "Agente físico",
  biological_agent:               "Agente biológico",
  physical_workload:              "Carga física",
  work_organization_psychosocial: "Org. del trabajo",
  order_cleanliness:              "Orden y limpieza",
  height_work:                    "Trabajos en altura",
  vehicles_forklifts:             "Vehículos / Montacargas",
  confined_space:                 "Espacio confinado",
  construction_work:              "Obra / Construcción",
  other:                          "Otro",
};

const DAMAGE_LABELS = {
  death:                              "Muerte",
  severe_injury_permanent_disability: "Lesión grave / inc. permanente",
  moderate_injury_lost_days:          "Lesión moderada / días perdidos",
  minor_injury_first_aid:             "Lesión leve / primeros auxilios",
  material_damage_no_injury:          "Daño material / sin lesión",
};

const STATUS_CONFIG = {
  IMPL: { labelKey: "ra_implemented",     color: "#2E9461", bg: "#D7EFE0" },
  PROG: { labelKey: "ra_in_progress_ctrl",color: "#3F7BC6", bg: "#DCE8F7" },
  PLAN: { labelKey: "ra_planned",         color: "#D4960F", bg: "#FEF3D6" },
  NONE: { labelKey: "ra_not_implemented", color: "#E04545", bg: "#FBE0E0" },
};

const METH_CONFIG = {
  "5x5":   { label: "M5×5",  cls: "m5x5"  },
  NTP330:  { label: "NTP",   cls: "ntp"   },
  INSST:   { label: "INSST", cls: "insst" },
};

// Matriz 5×5 local (para calcular valor residual si es necesario)
const MATRIX_5x5 = {
  5: { 1:"CRITICO",  2:"CRITICO",  3:"CRITICO",  4:"CRITICO",  5:"CRITICO"  },
  4: { 1:"MODERADO", 2:"MODERADO", 3:"MODERADO", 4:"ALTO",     5:"CRITICO"  },
  3: { 1:"MODERADO", 2:"MODERADO", 3:"MODERADO", 4:"ALTO",     5:"ALTO"     },
  2: { 1:"BAJO",     2:"BAJO",     3:"MODERADO", 4:"MODERADO", 5:"ALTO"     },
  1: { 1:"BAJO",     2:"BAJO",     3:"MODERADO", 4:"MODERADO", 5:"ALTO"     },
};
const INSST_MATRIX = {
  A: { ED:"CRITICO",  D:"ALTO",     LD:"MODERADO" },
  M: { ED:"ALTO",     D:"MODERADO", LD:"BAJO"     },
  B: { ED:"MODERADO", D:"BAJO",     LD:"BAJO"     },
};

function getRiskValue(r) {
  const m = r.methodology || "5x5";
  if (m === "NTP330") {
    if (r.nd && r.ne && r.nc) return r.nd * r.ne * r.nc;
    return r.riskLevel ?? "—";
  }
  if (m === "INSST") {
    if (r.probability && r.damageConsequence) return `${r.probability}×${r.damageConsequence}`;
    return "—";
  }
  return r.riskLevel ?? (r.severity && r.exposureFrequency ? Number(r.severity) * Number(r.exposureFrequency) : "—");
}

function getResidualLevel(r) {
  if (r.residualRisk) {
    if (["CRITICO","ALTO","MODERADO","BAJO"].includes(r.residualRisk)) return r.residualRisk;
    // backward compat con registros guardados con los strings viejos
    const MAP = {
      low_acceptable: "BAJO", moderate_monitor: "MODERADO",
      high_additional_action: "ALTO", critical_insufficient_control: "CRITICO",
    };
    if (MAP[r.residualRisk]) return MAP[r.residualRisk];
  }
  const m = r.methodology || "5x5";
  if (m === "5x5" && r.residualSeverity && r.residualFoe) {
    return MATRIX_5x5[Number(r.residualSeverity)]?.[Number(r.residualFoe)] || null;
  }
  if (m === "NTP330" && r.residualNd && r.residualNe && r.residualNc) {
    const nr = r.residualNd * r.residualNe * r.residualNc;
    if (nr >= 600) return "CRITICO";
    if (nr >= 150) return "ALTO";
    if (nr >= 40)  return "MODERADO";
    return "BAJO";
  }
  if (m === "INSST" && r.residualProbability && r.residualDamageConsequence) {
    return INSST_MATRIX[r.residualProbability]?.[r.residualDamageConsequence] || null;
  }
  return null;
}

function getResidualValue(r) {
  const m = r.methodology || "5x5";
  if (m === "5x5" && r.residualSeverity && r.residualFoe)
    return Number(r.residualSeverity) * Number(r.residualFoe);
  if (m === "NTP330" && r.residualNd && r.residualNe && r.residualNc)
    return r.residualNd * r.residualNe * r.residualNc;
  if (m === "INSST" && r.residualProbability && r.residualDamageConsequence)
    return `${r.residualProbability}×${r.residualDamageConsequence}`;
  return null;
}

// ── Subcomponentes ──────────────────────────────────────────
function MethBadge({ method }) {
  const m = method || "5x5";
  const cfg = METH_CONFIG[m] || METH_CONFIG["5x5"];
  return <span className={`rat-meth-badge ${cfg.cls}`}>{cfg.label}</span>;
}

function LevelPill({ level, levelConfig }) {
  if (!level || !levelConfig?.[level]) return <span style={{ color: "#bbb" }}>—</span>;
  const cfg = levelConfig[level];
  return (
    <span className="rat-lvl-pill" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="rat-lvl-dot" style={{ background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function StatusPill({ status }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <span className="rat-st-pill" style={{ background: cfg.bg, color: cfg.color }}>
      {t(cfg.labelKey)}
    </span>
  );
}

// ── Componente principal ────────────────────────────────────
export function RiskAssessmentsTable({ items, getLevel, levelConfig, onReload, onEdit, onDelete, role, extYear = null, transferSlot = null }) {
  const { accessToken, user } = useAuth();
  const { translations }      = useLanguage();
  const t = (key) => translations[key] || key;

  const canTransfer = isAdmin(role) || isMaster(role);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferYear, setTransferYear] = useState((extYear || new Date().getFullYear()) + 1);
  const [transferring, setTransferring] = useState(false);

  useEffect(() => { setSelectedIds(new Set()); }, [extYear, items]);

  const [openRowMenu, setOpenRowMenu] = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!openRowMenu) return;
    const handler = (e) => { if (!e.target.closest(".rat-table__row-menu, .rat-table__row-menu-list")) setOpenRowMenu(null); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openRowMenu]);

  const toggleRowMenu = (id, e) => {
    if (openRowMenu?.id === id) { setOpenRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 160;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setOpenRowMenu({
      id,
      left: rect.right - 190,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (items.length > 0 && prev.size === items.length) return new Set();
      return new Set(items.map((i) => i._id));
    });
  };

  const openTransfer = () => {
    setTransferYear((extYear || new Date().getFullYear()) + 1);
    setShowTransfer(true);
  };

  const transferInFlightRef = useRef(false);
  const handleTransfer = async () => {
    if (transferInFlightRef.current) return;
    transferInFlightRef.current = true;
    setTransferring(true);
    try {
      const result = await riskAssessmentController.copyYear(accessToken, Array.from(selectedIds), transferYear);
      const errorsSuffix = result.errors > 0 ? `, ${result.errors} ${t("ra_transfer_errors_count")}` : "";
      toast.success(`${t("ra_transfer_success")}: ${result.created} ${t("ra_transfer_created")}, ${result.skipped} ${t("ra_transfer_skipped")}${errorsSuffix}`);
      setShowTransfer(false);
      setSelectedIds(new Set());
      onReload();
    } catch {
      toast.error(t("ra_transfer_error"));
    } finally {
      transferInFlightRef.current = false;
      setTransferring(false);
    }
  };

  const [showObs,        setShowObs]        = useState(false);
  const [obsItem,        setObsItem]        = useState(null);
  const [obsVal,         setObsVal]         = useState("");
  const [savingObs,      setSavingObs]      = useState(false);
  const [editingObsIdx,  setEditingObsIdx]  = useState(null);
  const [editingObsVal,  setEditingObsVal]  = useState("");
  const [editingObsDate, setEditingObsDate] = useState("");

  const openObs = (item) => {
    setObsItem(item); setObsVal("");
    setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate("");
    setShowObs(true);
  };

  const handleSaveObs = async () => {
    if (!obsVal.trim()) return;
    setSavingObs(true);
    try {
      const userName  = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
      const newEntry = { text: obsVal.trim(), userName, createdAt: new Date() };
      const updatedObs = [...(obsItem.observations || []), newEntry];
      await riskAssessmentController.updateRiskAssessment(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setObsItem({ ...obsItem, observations: updatedObs });
      setObsVal("");
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSavingObs(false); }
  };

  const handleSaveObsEdit = async () => {
    if (!editingObsVal.trim()) return;
    setSavingObs(true);
    try {
      const updatedObs = (obsItem.observations || []).map((o, i) =>
        i === editingObsIdx
          ? { ...o, text: editingObsVal.trim(), createdAt: editingObsDate ? new Date(editingObsDate) : o.createdAt }
          : o
      );
      await riskAssessmentController.updateRiskAssessment(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      setObsItem((prev) => ({ ...prev, observations: updatedObs }));
      setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate("");
    } catch { toast.error(t("error_saving")); }
    finally { setSavingObs(false); }
  };

  const handleDeleteObs = async (origIdx) => {
    setSavingObs(true);
    try {
      const updatedObs = (obsItem.observations || []).filter((_, i) => i !== origIdx);
      await riskAssessmentController.updateRiskAssessment(accessToken, obsItem._id, { observations: updatedObs });
      toast.success(t("observations_saved"));
      onReload();
    } catch { toast.error(t("error_saving")); }
    finally { setSavingObs(false); }
  };

  if (!items) return null;

  return (
    <div className="rat-table">
      {canTransfer && transferSlot && ReactDOM.createPortal(
        <button
          className="ra-btn-outline"
          onClick={openTransfer}
          disabled={selectedIds.size === 0}
          title={selectedIds.size === 0 ? t("ra_transfer_none_selected") : undefined}
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="13" height="13">
            <path d="M5 4h7l-2-2m2 2-2 2M11 12H4l2 2m-2-2 2-2"/>
          </svg>
          {t("ra_transfer_button")}{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
        </button>,
        transferSlot
      )}
      {items.length === 0 ? (
        <div className="rat-empty">
          <p>{t("no_results")}</p>
        </div>
      ) : (
        <div className="rat-table__wrapper">
          <Table className="rat-table__tbl unstackable rtable-cards" selectable>
            <Table.Header>
              <Table.Row>
                {canTransfer && (
                  <Table.HeaderCell textAlign="center">
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedIds.size === items.length}
                      onChange={toggleSelectAll}
                    />
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell className="col-n">N°</Table.HeaderCell>
                <Table.HeaderCell className="col-meth">{t("ra_col_method")}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_sector_area")}</Table.HeaderCell>
                <Table.HeaderCell>{t("job_position") || "Puesto / Tarea"}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_hazard_source")}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_risk_type")}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_potential_damage")}</Table.HeaderCell>
                <Table.HeaderCell className="col-val">{t("ra_col_value")}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_initial_level")}</Table.HeaderCell>
                <Table.HeaderCell className="col-control">{t("ra_col_control_measure")}</Table.HeaderCell>
                <Table.HeaderCell className="col-val">Val. R</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_residual_level")}</Table.HeaderCell>
                <Table.HeaderCell>{t("status")}</Table.HeaderCell>
                <Table.HeaderCell>{t("ra_col_next_reassessment")}</Table.HeaderCell>
                <Table.HeaderCell textAlign="center"></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((r, idx) => {
                const level    = getLevel(r);
                const resLevel = getResidualLevel(r);
                const riskVal  = getRiskValue(r);
                const resVal   = getResidualValue(r);
                const nextDate = r.nextReassessment
                  ? new Date(r.nextReassessment).toLocaleDateString("es-AR")
                  : "—";

                return (
                  <Table.Row key={r._id}>
                    {canTransfer && (
                      <Table.Cell textAlign="center" data-label="">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r._id)}
                          onChange={() => toggleSelected(r._id)}
                        />
                      </Table.Cell>
                    )}
                    <Table.Cell className="col-n" data-label="N°">{r.customId || idx + 1}</Table.Cell>
                    <Table.Cell className="col-meth" data-label={t("ra_col_method")}>
                      <MethBadge method={r.methodology} />
                    </Table.Cell>
                    <Table.Cell data-label={t("ra_sector_area")}>{r.area || r.sector?.name || "—"}</Table.Cell>
                    <Table.Cell data-label={t("job_position") || "Puesto / Tarea"}>{r.jobPosition || "—"}</Table.Cell>
                    <Table.Cell data-label={t("ra_col_hazard_source")}>{HAZARD_LABELS[r.hazardSource] || r.hazardSource || "—"}</Table.Cell>
                    <Table.Cell data-label={t("ra_col_risk_type")}>{r.riskType || "—"}</Table.Cell>
                    <Table.Cell data-label={t("ra_col_potential_damage")}>{DAMAGE_LABELS[r.potentialDamage] || r.potentialDamage || "—"}</Table.Cell>
                    <Table.Cell className="col-val" data-label={t("ra_col_value")}>{riskVal}</Table.Cell>
                    <Table.Cell data-label={t("ra_col_initial_level")}>
                      <LevelPill level={level} levelConfig={levelConfig} />
                    </Table.Cell>
                    <Table.Cell className="col-control" data-label={t("ra_col_control_measure")}>
                      {r.specificControlMeasure
                        ? r.specificControlMeasure.length > 28
                          ? r.specificControlMeasure.slice(0, 28) + "…"
                          : r.specificControlMeasure
                        : "—"}
                    </Table.Cell>
                    <Table.Cell className="col-val" data-label="Val. R">{resVal ?? "—"}</Table.Cell>
                    <Table.Cell data-label={t("ra_col_residual_level")}>
                      <LevelPill level={resLevel} levelConfig={levelConfig} />
                    </Table.Cell>
                    <Table.Cell data-label={t("status")}>
                      <StatusPill status={r.controlStatus} />
                    </Table.Cell>
                    <Table.Cell data-label={t("ra_col_next_reassessment")}>{nextDate}</Table.Cell>
                    <Table.Cell textAlign="center" className="rat-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="rat-table__row-menu">
                        <button
                          type="button"
                          className="rat-table__row-menu-btn"
                          onClick={(e) => toggleRowMenu(r._id, e)}
                        >
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {openRowMenu?.id === r._id && ReactDOM.createPortal(
                        <div
                          className="rat-table__row-menu-list"
                          style={{
                            position: "fixed",
                            top: openRowMenu.top,
                            left: openRowMenu.left,
                            transform: openRowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { onEdit(r); setOpenRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openObs(r); setOpenRowMenu(null); }}>
                            <Icon name="comment outline" />{t("observations")}
                          </button>
                          <button className="rat-table__row-menu-item--danger" onClick={() => { onDelete(r); setOpenRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}

      {/* Modal observaciones */}
      {showObs && (
        <div className="rat-obs-backdrop" onClick={e => { if (e.target === e.currentTarget) { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); } }}>
          <div className="rat-obs-modal">
            <div className="rat-obs-header">
              <h3 className="rat-obs-title">{t("observations")}</h3>
              <button className="rat-obs-close" type="button" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="rat-obs-body">
        <div className="rat-table__modal-inner">
          {obsItem?.observations?.length > 0 && (
            <div className="rat-table__obs-history">
              {[...obsItem.observations].map((obs, origIdx) => ({ obs, origIdx })).reverse().map(({ obs, origIdx }) => {
                const currentUser = [user?.name, user?.lastname].filter(Boolean).join(" ") || "Usuario";
                const isOwner   = obs.userName === currentUser;
                const isEditing = editingObsIdx === origIdx;
                return (
                  <div key={origIdx} className="rat-table__obs-entry">
                    <span className="rat-table__obs-avatar" style={{ background: avatarColor(obs.userName) }}>
                      {getInitials(obs.userName)}
                    </span>
                    <div className="rat-table__obs-body">
                      <div className="rat-table__obs-meta">
                        <span className="rat-table__obs-user">{obs.userName}</span>
                        <span className="rat-table__obs-date">
                          {obs.createdAt ? new Date(obs.createdAt).toLocaleDateString() : ""}
                        </span>
                        {isOwner && !isEditing && (
                          <span className="rat-table__obs-actions">
                            <Icon name="pencil" link onClick={() => {
                              setEditingObsIdx(origIdx);
                              setEditingObsVal(obs.text);
                              setEditingObsDate(obs.createdAt ? new Date(obs.createdAt).toISOString().split("T")[0] : "");
                            }} />
                            <Icon name="trash alternate" link color="red" onClick={() => handleDeleteObs(origIdx)} />
                          </span>
                        )}
                      </div>
                      {isEditing ? (
                        <div>
                          <TextArea
                            rows={2}
                            value={editingObsVal}
                            onChange={e => setEditingObsVal(e.target.value)}
                            style={{ width: "100%", marginBottom: "0.5rem" }}
                          />
                          <div style={{ display: "flex", gap: "0.4rem" }}>
                            <button type="button" disabled={savingObs || !editingObsVal.trim()} onClick={handleSaveObsEdit} className="obs-icon-btn obs-icon-btn--confirm">
                              <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 6.5L5 9.5L11 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </button>
                            <button type="button" onClick={() => { setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }} className="obs-icon-btn obs-icon-btn--cancel">
                              <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M8.5 2.5L2.5 8.5M2.5 2.5L8.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rat-table__obs-text">{obs.text}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <TextArea
            rows={3}
            value={obsVal}
            onChange={e => setObsVal(e.target.value)}
            placeholder={t("enter_observations")}
            style={{ width: "100%", marginTop: "0.75rem" }}
          />
          <div className="rat-table__modal-actions">
            <button className="rat-obs-btn-confirm" disabled={savingObs || !obsVal.trim()} onClick={handleSaveObs}>
              {savingObs ? "..." : t("save")}
            </button>
            <button className="rat-obs-btn-cancel" onClick={() => { setShowObs(false); setEditingObsIdx(null); setEditingObsVal(""); setEditingObsDate(""); }}>{t("cancel")}</button>
          </div>
        </div>
            </div>
          </div>
        </div>
      )}

      {showTransfer && (
        <div className="rat-obs-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setShowTransfer(false); }}>
          <div className="rat-obs-modal">
            <div className="rat-obs-header">
              <h3 className="rat-obs-title">{t("ra_transfer_title")}</h3>
              <button className="rat-obs-close" type="button" onClick={() => setShowTransfer(false)}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="rat-obs-body">
              <p className="rat-obs-transfer-text">
                {t("ra_transfer_confirm_prefix")} {selectedIds.size} {t("ra_transfer_confirm_middle")}{" "}
                <select
                  className="rat-obs-transfer-select"
                  value={transferYear}
                  onChange={(e) => setTransferYear(Number(e.target.value))}
                >
                  {[1, 2, 3].map((offset) => {
                    const y = (extYear || new Date().getFullYear()) + offset;
                    return <option key={y} value={y}>{y}</option>;
                  })}
                </select>
                . {t("ra_transfer_confirm_suffix")}
              </p>
              <div className="rat-table__modal-actions">
                <button className="rat-obs-btn-confirm" disabled={transferring} onClick={handleTransfer}>
                  {transferring ? "…" : t("ra_transfer_confirm_btn")}
                </button>
                <button className="rat-obs-btn-cancel" onClick={() => setShowTransfer(false)}>{t("cancel")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
